import { KeyboardEvent, useCallback } from 'react';
import { InternalParticipant } from '@/types/api';
import { FieldValues, UseFormSetValue } from 'react-hook-form';

interface UseAttenderKeyboardProps<T extends FieldValues = any> {
  isFocus: boolean;
  debouncedValue: string;
  searchString: string;
  searchData: InternalParticipant[];
  attenderList: InternalParticipant[];
  searchFocusIndex: number;
  attenderFocusIndex: number;
  registerName: string;
  setSearchFocusIndex: (value: number | ((prev: number) => number)) => void;
  setAttenderFocusIndex: (value: number | ((prev: number) => number)) => void;
  setIsFocus: (value: boolean) => void;
  setValue: UseFormSetValue<T>;
  handleTogglePassport: (passport: InternalParticipant) => void;
  handleDelete: (passportId: string) => void;
}

export const useAttenderKeyboard = <T extends FieldValues = any>({
  isFocus,
  debouncedValue,
  searchString,
  searchData,
  attenderList,
  searchFocusIndex,
  attenderFocusIndex,
  registerName,
  setSearchFocusIndex,
  setAttenderFocusIndex,
  setIsFocus,
  setValue,
  handleTogglePassport,
  handleDelete,
}: UseAttenderKeyboardProps<T>) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const isSearchListOpen =
        isFocus && debouncedValue.length > 0 && searchData.length > 0;

      if (isSearchListOpen) {
        // SearchList navigation
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            setSearchFocusIndex((prev) =>
              prev < searchData.length - 1 ? prev + 1 : prev,
            );
            setAttenderFocusIndex(-1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            setSearchFocusIndex((prev) => (prev > 0 ? prev - 1 : 0));
            setAttenderFocusIndex(-1);
            break;
          case 'Enter':
            event.preventDefault();
            if (searchFocusIndex >= 0 && searchData[searchFocusIndex]) {
              handleTogglePassport(searchData[searchFocusIndex]);
            }
            break;
          case 'Escape':
            setSearchFocusIndex(-1);
            setIsFocus(false);
            setValue(registerName as any, '' as any);
            break;
        }
      } else {
        // AttenderItem navigation
        switch (event.key) {
          case 'ArrowLeft':
            if (searchString.length === 0) {
              event.preventDefault();
              if (attenderList.length > 0) {
                setAttenderFocusIndex((prev) => {
                  if (prev === -1) return attenderList.length - 1;
                  return prev > 0 ? prev - 1 : 0;
                });
              }
            }
            break;
          case 'ArrowRight':
            if (searchString.length === 0) {
              event.preventDefault();
              if (attenderList.length > 0) {
                setAttenderFocusIndex((prev) => {
                  if (prev === -1) return 0;
                  return prev < attenderList.length - 1 ? prev + 1 : prev;
                });
              }
            }
            break;
          case 'Delete':
          case 'Backspace':
            if (searchString.length === 0) {
              event.preventDefault();
              if (attenderFocusIndex >= 0 && attenderList[attenderFocusIndex]) {
                // Delete focused item
                handleDelete(attenderList[attenderFocusIndex].passportId);
              } else if (attenderList.length > 0) {
                // Delete last item
                handleDelete(attenderList[attenderList.length - 1].passportId);
              }
            }
            break;
          case 'Escape':
            setAttenderFocusIndex(-1);
            break;
        }
      }
    },
    [
      isFocus,
      debouncedValue,
      searchString,
      searchData,
      attenderList,
      searchFocusIndex,
      attenderFocusIndex,
      registerName,
      setSearchFocusIndex,
      setAttenderFocusIndex,
      setIsFocus,
      setValue,
      handleTogglePassport,
      handleDelete,
    ],
  );

  return { handleKeyDown };
};
